import {defineStore} from 'pinia'
import {computed} from 'vue'
import {useSessionStorage} from '@vueuse/core'
import {PermissionEntityType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionEntityType'
import {useViewAsApiStore} from '@toolify/client/src/stores/api/ViewAsApiStore/useViewAsApiStore'
import {DefaultViewAsState} from './constants/DefaultViewAsState'
import {ViewAsStorageKey} from './constants/ViewAsStorageKey'
import {writeViewAsStateToStorage} from './functions/writeViewAsStateToStorage'
import {IViewAsState} from './types/IViewAsState'

export const useViewAsStore = defineStore('viewAs', () => {
  const state = useSessionStorage<IViewAsState>(ViewAsStorageKey, {...DefaultViewAsState})

  const isActive = computed(() => Boolean(state.value.token))
  const targetLabel = computed(() => state.value.targetLabel)
  const targetEntityType = computed(() => state.value.targetEntityType)
  const expiresAt = computed(() => (state.value.expiresAt ? new Date(state.value.expiresAt) : null))

  function clear() {
    state.value = {...DefaultViewAsState}
    writeViewAsStateToStorage(state.value)
  }

  async function endSession(sessionId: string | null): Promise<void> {
    if(!sessionId) {
      return
    }
    await useViewAsApiStore().endViewAsSession(sessionId).catch(() => undefined)
  }

  async function start(params: {
    workspaceId: string,
    targetEntityType: PermissionEntityType,
    targetEntityId: string,
    targetLabel: string,
  }): Promise<void> {
    const previousSessionId = state.value.sessionId
    const {token, session} = await useViewAsApiStore().startViewAsSession(
      params.workspaceId,
      params.targetEntityType,
      params.targetEntityId,
    )
    await endSession(previousSessionId)
    state.value = {
      token,
      sessionId: session.sessionId,
      workspaceId: params.workspaceId,
      targetEntityType: params.targetEntityType,
      targetEntityId: params.targetEntityId,
      targetLabel: params.targetLabel,
      expiresAt: session.expiresAt ? new Date(session.expiresAt).toISOString() : null,
    }
    writeViewAsStateToStorage(state.value)
    window.location.reload()
  }

  async function stop(targetHref?: string): Promise<void> {
    await endSession(state.value.sessionId)
    clear()
    if(targetHref) {
      window.history.pushState(null, '', targetHref)
    }
    window.location.reload()
  }

  return {
    state,
    isActive,
    targetLabel,
    targetEntityType,
    expiresAt,
    start,
    stop,
    clear,
  }
})
